import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Outlet, useMatches } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDocumentTitle } from "../lib/documentTitle";

// The page title channel: how a route tells the shell what its <h1> reads.
//
// MECHANISM, and why (decision 12.9.2026)
// ---------------------------------------
// The title is a ROUTE `handle`, read by the shell with `useMatches()` — not a <PageTitle>
// component a page renders into a portal. Three reasons, in order of weight:
//
//   1. The bar must have a title BEFORE the page renders anything. A detail route spends its
//      first frames in a loading state (CourseEditPage returns a bare loading paragraph), and a
//      component-based channel can only speak once the page has rendered — so the bar would be
//      empty exactly when the user most needs to know where they are, and would then pop the
//      title in. The handle is part of the route match, so the title is known at navigation time.
//   2. It is declarative and it is in one place. The route table already states what each URL is,
//      who may open it and which component serves it; the title is the same kind of fact, and
//      reading router.tsx answers "what does this page call itself" without opening 25 pages.
//   3. It cannot render twice or leak. A portal/context component gets its ordering from the
//      React tree, so two of them (a page and a nested panel) silently fight; a match has exactly
//      one deepest entry.
//
// The cost of the handle is that it is STATIC — `t("users.title")`, never "Tulityökortti 2026".
// So routes whose title is data carry BOTH: the handle gives the generic standing title, and the
// page calls `usePageTitle(course.name)` once it holds the data. That is an override on the same
// channel, not a second mechanism: the shell resolves override ?? handle ?? nothing.
//
// LOADING STATE
// -------------
// While a detail route loads, the bar reads the handle's generic title ("Koulutus"), because the
// alternatives are worse: an empty bar means the shell flickers its heading in and out on every
// navigation (and a heading that appears late is one a screen-reader user has already walked past),
// and a skeleton bar would animate chrome that is not waiting for anything. The specific name
// replaces the generic one when the data lands. A route that supplies NEITHER handle nor override
// renders no <h1> at all — an empty heading is worse than no heading for assistive tech.

/** What a route puts in its `handle` to name itself. The value is an i18n key, not a string:
 *  the title has to re-render in the new language when the language switcher is used. */
export type RouteTitleHandle = { titleKey: string };

function titleKeyOf(handle: unknown): string | null {
  if (typeof handle !== "object" || handle === null) return null;
  const { titleKey } = handle as Partial<RouteTitleHandle>;
  return typeof titleKey === "string" && titleKey.length > 0 ? titleKey : null;
}

// THE BROWSER TAB reads from this same channel (task e46750d5), which is why the channel's state
// sits at the ROUTER ROOT (`PageTitleRoot` below) rather than inside AppLayout. The tab has to be
// written on every route the SPA answers, and the pre-session pages (login, the email-link pair, the
// OIDC callback) render outside the shell — a channel owned by AppLayout could not reach them, and
// leaving them out would mean a login tab still carrying the title of the page signed out of.
// So those routes carry a `handle` too. It draws no <h1> anywhere (there is no top bar out there;
// AuthLayout's own heading is still the page's) — it names the tab, which is the one thing those
// pages had no way to say.

const PageTitleOverrideContext = createContext<((title: string | null) => void) | null>(null);
const PageTitleValueContext = createContext<string | null>(null);

/**
 * Override the route handle's static title with one the page only knows once it has data
 * (a course name, a certificate holder). Pass null/undefined while the data is still loading —
 * the handle's generic title then stands. The override is dropped when the page unmounts, so a
 * route cannot leave its title behind on the next one.
 */
export function usePageTitle(title: string | null | undefined): void {
  const setOverride = useContext(PageTitleOverrideContext);
  useEffect(() => {
    if (!setOverride) return;
    setOverride(title && title.trim().length > 0 ? title : null);
    return () => setOverride(null);
  }, [setOverride, title]);
}

/**
 * Router-side half of the channel, and the element of the pathless route every other route hangs
 * under (see ./router). It resolves the title, writes the browser tab, hands the override setter
 * down to the pages and the resolved title to the shell. Used once, by the router.
 *
 * `appNameKey` names the suffix every tab title carries. It defaults to the product's name; the
 * operator console (apps/platform, ADR-0014 §6 phase 2) passes its own, because a tab reading
 * "Verso People" would name the product a platform operator is not looking at. An i18n KEY and not a
 * string, for the same reason a route handle is one: the router is built once, at import time, where
 * there is no `t` and no current language — so a string would freeze the suffix in whichever language
 * happened to be active then, and the tab would stop following the language switcher.
 */
export function PageTitleRoot({ appNameKey = "app.title" }: { appNameKey?: string } = {}): ReactNode {
  const { t } = useTranslation();
  const matches = useMatches();
  const [override, setOverride] = useState<string | null>(null);

  // The DEEPEST match that names itself wins: a nested route is more specific than its parent.
  const handleTitle = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const key = titleKeyOf(matches[i]?.handle);
      if (key) return t(key);
    }
    return null;
  }, [matches, t]);

  const title = override ?? handleTitle;
  const tabSuffix = t(appNameKey);

  // The tab. Written on EVERY render of the resolved title, so it follows a navigation, a
  // usePageTitle() override landing with the data, and the language switcher alike; a route that
  // names nothing gets the bare app name rather than the last route's title (../lib/documentTitle).
  useEffect(() => {
    document.title = formatDocumentTitle(title, tabSuffix);
  }, [title, tabSuffix]);

  return (
    <PageTitleOverrideContext.Provider value={setOverride}>
      <PageTitleValueContext.Provider value={title}>
        <Outlet />
      </PageTitleValueContext.Provider>
    </PageTitleOverrideContext.Provider>
  );
}

/** The resolved title of the current route, for the shell's <h1>. Null when the route names
 *  nothing — AppLayout then renders no heading at all, not an empty one. */
export function usePageTitleValue(): string | null {
  return useContext(PageTitleValueContext);
}

/** The id of the shell's <h1>. A page whose root <section> used to point `aria-labelledby` at its
 *  own heading points it here instead, so the region keeps its accessible name. */
export const PAGE_TITLE_ID = "page-title";
